import ky, {ResponsePromise} from 'ky';

import {LoginResponse} from '~redux/types/user';

import {getOptions} from './api';

const AUTH_API = import.meta.env.VITE_AUTH_API ?? '';

export const login = async (email: string, password: string): Promise<LoginResponse> => {
  const data = {email: email, password: password};
  return ky
    .post(`${AUTH_API}/auth/login`, getOptions({signalId: 'login', data, checkAuth: false}))
    .json<LoginResponse>();
};

export const logout = (token: string): ResponsePromise => {
  const headers = new Headers();
  headers.append('Authorization', `Bearer ${token}`);

  return ky.delete(`${AUTH_API}/auth/logout`, {headers: headers, timeout: 120 * 1000});
};

export const exchangeWorkOSCode = async (code: string, state?: string): Promise<LoginResponse> => {
  const data = {code, state};
  return ky
    .post(`${AUTH_API}/auth/exchange_code`, getOptions({signalId: 'workos-callback', data, checkAuth: false}))
    .json<LoginResponse>();
};

export const switchCustomer = async (customer: string): Promise<LoginResponse> => {
  return ky
    .post(`${AUTH_API}/auth/switch-customer`, getOptions({signalId: 'switchCustomer', data: {customer}}))
    .json<LoginResponse>();
};

export const getLogoutUrl = async (): Promise<{logoutUrl: string}> => {
  return ky
    .get(
      `${AUTH_API}/auth/logout/url`,
      getOptions({
        signalId: 'workos-logout-url',
      }),
    )
    .json<{logoutUrl: string}>();
};

export const getWidgetToken = async (): Promise<{token: string}> => {
  return ky
    .get(
      `${AUTH_API}/auth/widget-token`,
      getOptions({
        signalId: 'workos-widget-token',
      }),
    )
    .json<{token: string}>();
};

// Short-lived, audience-scoped token for the embedded Quality Agent iframe.
// The Quality Agent verifies it against the API's published JWKS.
//
// The API camelCases response keys (BaseSchema alias_generator), so the expiry
// arrives as `expiresAt` — destructuring `expires_at` here silently yielded
// undefined and disabled the iframe's proactive token refresh.
//
// Passes an independent (never-aborted) signal via `reactQuerySignal` so this
// request bypasses getOptions' shared per-signalId AbortController map: the
// handshake can legitimately fire two token fetches in quick succession (React
// StrictMode double-mounts the iframe / an initial fetch racing a refresh) and
// the default map would abort the first, surfacing an uncaught AbortError.
export const getQualityAgentToken = async (): Promise<{token: string; expiresAt: string}> => {
  return ky
    .get(
      `${AUTH_API}/auth/embed/quality-agent/token`,
      getOptions({
        signalId: 'quality-agent-token',
        reactQuerySignal: new AbortController().signal,
      }),
    )
    .json<{token: string; expiresAt: string}>();
};
